import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { POLLING_INTERVAL_MS } from "../../constants/polling-constants";
import {
  getUmProviderNpi,
  UM_API_STATUS,
  UM_RESPONSE_STATUS,
} from "../../constants/um-constants";
import { queryKeys } from "../../lib/queryKeys";
import {
  getUmQueriesList,
  submitUmQueryResponse,
} from "../../services";
import type {
  UmQueriesListResponse,
  UmQuerySubmitPayload,
} from "../../types/um-queries";
import { extractUmQueriesListResponse } from "../../utils/um-query-utils";

export interface UmQueryListParams {
  provider_npi: string;
}

const fetchUmQueriesList = async (
  providerNpi: string
): Promise<UmQueriesListResponse> => {
  const response = await getUmQueriesList({
    provider_npi: getUmProviderNpi(providerNpi),
  });

  const payload = extractUmQueriesListResponse(response?.data);

  // TODO: remove — temporary debug log for decrypted UM queries list response
  console.log("[UM Queries] decrypted list response:", payload);

  return payload;
};

const fetchUmQueriesOpenCount = async (providerNpi: string): Promise<number> => {
  const payload = await fetchUmQueriesList(providerNpi);

  return (payload.queries ?? []).filter(
    (query) => query.status === UM_API_STATUS.OPEN
  ).length;
};

export const useUmQueryList = (
  params: UmQueryListParams,
  options?: { polling?: boolean; enabled?: boolean }
) => {
  const polling = options?.polling ?? false;
  const enabled = options?.enabled ?? Boolean(params.provider_npi);

  const requestParams = {
    provider_npi: getUmProviderNpi(params.provider_npi),
  };

  return useQuery({
    queryKey: queryKeys.umQueriesList(requestParams),
    queryFn: () => fetchUmQueriesList(params.provider_npi),
    enabled: enabled && Boolean(params.provider_npi),
    refetchInterval: polling ? POLLING_INTERVAL_MS : false,
  });
};

export const useUmQueriesOpenCount = (
  providerNpi?: string,
  options?: { polling?: boolean; enabled?: boolean }
) => {
  const polling = options?.polling ?? false;
  const enabled = options?.enabled ?? Boolean(providerNpi);

  return useQuery({
    queryKey: queryKeys.umQueriesOpenCount(providerNpi ?? ""),
    queryFn: () => fetchUmQueriesOpenCount(providerNpi!),
    enabled: enabled && Boolean(providerNpi),
    refetchInterval: polling ? POLLING_INTERVAL_MS : false,
  });
};

export const useSubmitUmQuery = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      queryId,
      body,
    }: {
      queryId: string;
      body: UmQuerySubmitPayload;
    }) => submitUmQueryResponse(queryId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["umQueriesList"] });
      void queryClient.invalidateQueries({ queryKey: ["umQueriesOpenCount"] });
    },
  });
};

export const fileToDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export { UM_RESPONSE_STATUS };
